import { a98ToRgb, cmykToRgb, hslToRgb, hwbToRgb, labToRgb, lchToRgb, rgb100ToRgb, rgbToA98, rgbToCmyk, rgbToHex, rgbToHsl, rgbToHwb, rgbToLab, rgbToLch } from "./colorTranslation.mjs";
import { ColorFormat } from "./enum.mjs";
import { merge } from "./helper.mjs";
import { oklabToRgb, oklchToRgb, rgbToOklab, rgbToOklch } from "./okColors.mjs";
import { standardizePartialA98, standardizePartialCmyk, standardizePartialHsl, standardizePartialHwb, standardizePartialLab, standardizePartialLch, standardizePartialOklab, standardizePartialOklch, standardizePartialRgb } from "./standardize.mjs";
import { a98ToString, cmykToString, hex0xToString, hexToString, hslToString, hwbToString, labToString, lchToString, oklabToString, oklchToString, rgbToString } from "./stringify.mjs";
import { colorToRgb100 } from "./utils.mjs";
export default class ColorTranslator {
  _options = {
    angleUnit: "none",
    legacy: false,
    spaced: true,
    limitToColorSpace: true,
    maxDigits: 2,
    cacheInput: true
  };
  constructor(color, options = {}) {
    this._options = merge(this._options, options);
    const {
      rgb100,
      format,
      standardizedColor
    } = colorToRgb100(color);
    this.setCachedInput(format, standardizedColor);
    this._rgb = rgb100;
  }
  cachedInput(format) {
    var _this$_lastInput;
    return this._options.cacheInput && ((_this$_lastInput = this._lastInput) === null || _this$_lastInput === void 0 ? void 0 : _this$_lastInput.format) === format ? this._lastInput : undefined;
  }
  setCachedInput(format, color) {
    this._lastInput = {
      format,
      color
    };
  }

  // HEX

  get hex() {
    var _ref, _this$cachedInput;
    const rgb100 = (_ref = (_this$cachedInput = this.cachedInput(ColorFormat.RGB)) === null || _this$cachedInput === void 0 ? void 0 : _this$cachedInput.color) !== null && _ref !== void 0 ? _ref : this._rgb;
    return {
      ...rgbToHex(rgb100ToRgb(rgb100)),
      toString: hexToString,
      options: this._options
    };
  }

  // HEX 0x

  get hex0x() {
    var _ref2, _this$cachedInput2;
    const rgb100 = (_ref2 = (_this$cachedInput2 = this.cachedInput(ColorFormat.RGB)) === null || _this$cachedInput2 === void 0 ? void 0 : _this$cachedInput2.color) !== null && _ref2 !== void 0 ? _ref2 : this._rgb;
    return {
      ...rgbToHex(rgb100ToRgb(rgb100)),
      toString: hex0xToString,
      options: this._options
    };
  }

  // RGB

  get rgb() {
    var _ref3, _this$cachedInput3;
    const rgb100 = (_ref3 = (_this$cachedInput3 = this.cachedInput(ColorFormat.RGB)) === null || _this$cachedInput3 === void 0 ? void 0 : _this$cachedInput3.color) !== null && _ref3 !== void 0 ? _ref3 : this._rgb;
    return {
      ...rgb100ToRgb(rgb100),
      toString: rgbToString,
      options: this._options
    };
  }
  updateRgb(rgbRaw) {
    var _ref4, _this$cachedInput4;
    const rgb100 = (_ref4 = (_this$cachedInput4 = this.cachedInput(ColorFormat.RGB)) === null || _this$cachedInput4 === void 0 ? void 0 : _this$cachedInput4.color) !== null && _ref4 !== void 0 ? _ref4 : this._rgb;
    const rgb = standardizePartialRgb(rgbRaw);
    this._rgb = merge(rgb100, rgb);
    this.setCachedInput(ColorFormat.RGB, this._rgb);
    return this;
  }

  // HSL

  get hsl() {
    var _ref5, _this$cachedInput5;
    const hsl = (_ref5 = (_this$cachedInput5 = this.cachedInput(ColorFormat.HSL)) === null || _this$cachedInput5 === void 0 ? void 0 : _this$cachedInput5.color) !== null && _ref5 !== void 0 ? _ref5 : rgbToHsl(this._rgb);
    return {
      ...hsl,
      toString: hslToString,
      options: this._options
    };
  }
  updateHsl(hslRaw) {
    var _ref6, _this$cachedInput6;
    const hsl = standardizePartialHsl(hslRaw);
    const currentHsl = (_ref6 = (_this$cachedInput6 = this.cachedInput(ColorFormat.HSL)) === null || _this$cachedInput6 === void 0 ? void 0 : _this$cachedInput6.color) !== null && _ref6 !== void 0 ? _ref6 : rgbToHsl(this._rgb);
    const newHsl = merge(currentHsl, hsl);
    this.setCachedInput(ColorFormat.HSL, newHsl);
    const rgb = hslToRgb(newHsl);
    this._rgb = rgb;
    return this;
  }

  // HWB

  get hwb() {
    var _ref7, _this$cachedInput7;
    const hwb = (_ref7 = (_this$cachedInput7 = this.cachedInput(ColorFormat.HWB)) === null || _this$cachedInput7 === void 0 ? void 0 : _this$cachedInput7.color) !== null && _ref7 !== void 0 ? _ref7 : rgbToHwb(this._rgb);
    return {
      ...hwb,
      toString: hwbToString,
      options: this._options
    };
  }
  updateHwb(hwbRaw) {
    var _ref8, _this$cachedInput8;
    const hwb = standardizePartialHwb(hwbRaw);
    const currentHwb = (_ref8 = (_this$cachedInput8 = this.cachedInput(ColorFormat.HWB)) === null || _this$cachedInput8 === void 0 ? void 0 : _this$cachedInput8.color) !== null && _ref8 !== void 0 ? _ref8 : rgbToHwb(this._rgb);
    const newHwb = merge(currentHwb, hwb);
    this.setCachedInput(ColorFormat.HWB, newHwb);
    const rgb = hwbToRgb(newHwb);
    this._rgb = rgb;
    return this;
  }

  // LAB

  get lab() {
    var _ref9, _this$cachedInput9;
    const lab = (_ref9 = (_this$cachedInput9 = this.cachedInput(ColorFormat.LAB)) === null || _this$cachedInput9 === void 0 ? void 0 : _this$cachedInput9.color) !== null && _ref9 !== void 0 ? _ref9 : rgbToLab(this._rgb);
    return {
      ...lab,
      toString: labToString,
      options: this._options
    };
  }
  updateLab(labRaw) {
    var _ref10, _this$cachedInput10;
    const lab = standardizePartialLab(labRaw);
    const currentLab = (_ref10 = (_this$cachedInput10 = this.cachedInput(ColorFormat.LAB)) === null || _this$cachedInput10 === void 0 ? void 0 : _this$cachedInput10.color) !== null && _ref10 !== void 0 ? _ref10 : rgbToLab(this._rgb);
    const newLab = merge(currentLab, lab);
    this.setCachedInput(ColorFormat.LAB, newLab);
    const rgb = labToRgb(newLab);
    this._rgb = rgb;
    return this;
  }

  // LCH

  get lch() {
    var _ref11, _this$cachedInput11;
    const lch = (_ref11 = (_this$cachedInput11 = this.cachedInput(ColorFormat.LCH)) === null || _this$cachedInput11 === void 0 ? void 0 : _this$cachedInput11.color) !== null && _ref11 !== void 0 ? _ref11 : rgbToLch(this._rgb);
    return {
      ...lch,
      toString: lchToString,
      options: this._options
    };
  }
  updateLch(lchRaw) {
    var _ref12, _this$cachedInput12;
    const lch = standardizePartialLch(lchRaw);
    const currentLch = (_ref12 = (_this$cachedInput12 = this.cachedInput(ColorFormat.LCH)) === null || _this$cachedInput12 === void 0 ? void 0 : _this$cachedInput12.color) !== null && _ref12 !== void 0 ? _ref12 : rgbToLch(this._rgb);
    const newLch = merge(currentLch, lch);
    this.setCachedInput(ColorFormat.LCH, newLch);
    const rgb = lchToRgb(newLch);
    this._rgb = rgb;
    return this;
  }

  // LAB

  get oklab() {
    var _ref13, _this$cachedInput13;
    const oklab = (_ref13 = (_this$cachedInput13 = this.cachedInput(ColorFormat.OKLAB)) === null || _this$cachedInput13 === void 0 ? void 0 : _this$cachedInput13.color) !== null && _ref13 !== void 0 ? _ref13 : rgbToOklab(this._rgb);
    return {
      ...oklab,
      toString: oklabToString,
      options: this._options
    };
  }
  updateOklab(oklabRaw) {
    var _ref14, _this$cachedInput14;
    const oklab = standardizePartialOklab(oklabRaw);
    const currentOklab = (_ref14 = (_this$cachedInput14 = this.cachedInput(ColorFormat.OKLAB)) === null || _this$cachedInput14 === void 0 ? void 0 : _this$cachedInput14.color) !== null && _ref14 !== void 0 ? _ref14 : rgbToOklab(this._rgb);
    const newOklab = merge(currentOklab, oklab);
    this.setCachedInput(ColorFormat.OKLAB, newOklab);
    const rgb = oklabToRgb(newOklab);
    this._rgb = rgb;
    return this;
  }

  // LCH

  get oklch() {
    var _ref15, _this$cachedInput15;
    const oklch = (_ref15 = (_this$cachedInput15 = this.cachedInput(ColorFormat.OKLCH)) === null || _this$cachedInput15 === void 0 ? void 0 : _this$cachedInput15.color) !== null && _ref15 !== void 0 ? _ref15 : rgbToOklch(this._rgb);
    return {
      ...oklch,
      toString: oklchToString,
      options: this._options
    };
  }
  updateOklch(oklchRaw) {
    var _ref16, _this$cachedInput16;
    const oklch = standardizePartialOklch(oklchRaw);
    const currentokLch = (_ref16 = (_this$cachedInput16 = this.cachedInput(ColorFormat.OKLCH)) === null || _this$cachedInput16 === void 0 ? void 0 : _this$cachedInput16.color) !== null && _ref16 !== void 0 ? _ref16 : rgbToOklch(this._rgb);
    const newOkLch = merge(currentokLch, oklch);
    this.setCachedInput(ColorFormat.OKLCH, newOkLch);
    const rgb = oklchToRgb(newOkLch);
    this._rgb = rgb;
    return this;
  }

  // CMYK

  get cmyk() {
    var _ref17, _this$cachedInput17;
    const cmyk = (_ref17 = (_this$cachedInput17 = this.cachedInput(ColorFormat.DEVICE_CMYK)) === null || _this$cachedInput17 === void 0 ? void 0 : _this$cachedInput17.color) !== null && _ref17 !== void 0 ? _ref17 : rgbToCmyk(this._rgb);
    return {
      ...cmyk,
      toString: cmykToString,
      options: this._options
    };
  }
  updateCmyk(cmykRaw) {
    var _ref18, _this$cachedInput18;
    const cmyk = standardizePartialCmyk(cmykRaw);
    const currentCmyk = (_ref18 = (_this$cachedInput18 = this.cachedInput(ColorFormat.DEVICE_CMYK)) === null || _this$cachedInput18 === void 0 ? void 0 : _this$cachedInput18.color) !== null && _ref18 !== void 0 ? _ref18 : rgbToCmyk(this._rgb);
    const newCmyk = merge(currentCmyk, cmyk);
    this.setCachedInput(ColorFormat.DEVICE_CMYK, newCmyk);
    const rgb = cmykToRgb(newCmyk);
    this._rgb = rgb;
    return this;
  }

  // A98

  get a98() {
    var _ref19, _this$cachedInput19;
    const a98 = (_ref19 = (_this$cachedInput19 = this.cachedInput(ColorFormat.A98)) === null || _this$cachedInput19 === void 0 ? void 0 : _this$cachedInput19.color) !== null && _ref19 !== void 0 ? _ref19 : rgbToA98(this._rgb);
    return {
      ...a98,
      toString: a98ToString,
      options: this._options
    };
  }
  updateA98(a98Raw) {
    var _ref20, _this$cachedInput20;
    const a98 = standardizePartialA98(a98Raw);
    const currentA98 = (_ref20 = (_this$cachedInput20 = this.cachedInput(ColorFormat.A98)) === null || _this$cachedInput20 === void 0 ? void 0 : _this$cachedInput20.color) !== null && _ref20 !== void 0 ? _ref20 : rgbToA98(this._rgb);
    const newA98 = merge(currentA98, a98);
    this.setCachedInput(ColorFormat.A98, newA98);
    const rgb = a98ToRgb(newA98);
    this._rgb = rgb;
    return this;
  }

  // Options

  get options() {
    return this._options;
  }
  updateOptions(options) {
    this._options = merge(this._options, options);
    return this;
  }
}